import React, { useState, useEffect, useRef } from 'react';
import { Formik, Field } from 'formik';
import { IndicatorIcon } from '../../assets/svg/IndicatorIcon';
import { TitleText, LabelText } from '../../ui/Text';
import { PositiveChangeIcon } from '../../assets/svg/PositiveChangeIcon';
import { DropDown } from '../../ui/DropDown';
import { Button } from '../../ui/Button';
import { NumberInput } from '../../ui/Input';
import styled, { keyframes } from 'styled-components';
import { get } from '../../utils/get';
import CommentIcon from '../../assets/svg/CommentIcon';
import Modal from '../../ui/Modal';
import CommentsModal from '../../ui/CommentsModal';
import numeral from 'numeral';
import Axios from '../../utils/axios';
import { toast } from 'react-toastify';
import { SCORECARD_KPI_TARGET, KPI_TARGET } from '../../api/urls';
import ADAReferenceModal from '../../ui/AdaModal';
import { useEvidence } from '../../context/EvidenceContext';

interface IProps {
  kpi: any;
  handleSubmit: (values: any) => void;
  reload?: any;
}

export const Measure = ({ kpi, handleSubmit, reload }: IProps) => {
  const { setEvidenceData, setEvidenceType } = useEvidence();
  const [initialValu, setInitialValu] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'comment' | 'ada' | null>(null);
  const [targets, setTargets] = useState<any>([]);
  const [isCommentAccessed, setIsCommentAccessed] = useState(false);
  const [hasAttachedFiles, setHasAttachedFiles] = useState(false);
  const [adaEvidenceProvided, setAdaEvidenceProvided] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOptionsOpen(false); // Close the dropdown
    }
  };

  useEffect(() => {
    if (isOptionsOpen) {
      // Add event listener when the dropdown is open
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      // Remove event listener when the dropdown is closed
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOptionsOpen]);

  let kpiId = get(kpi, 'id', []);

  useEffect(() => {
    function fetchTargets() {
      Axios.get(`${KPI_TARGET}?scorecard_kpi_id=${kpiId}`).then(
        res => {
          const { returned_resultset } = res.data;
          setTargets(returned_resultset);
            })
            .catch(err => {
                console.error("Error fetching targets: ", err);
            });
    }
    fetchTargets();
  }, [kpiId, reload]);

  let kpiTargets = targets.map(kpiTarget => ({
    value: get(kpiTarget, 'id', ''),
    label: get(kpiTarget, 'label', '')
  }));

  const handleReset = (resetValues) => {
    const kpiTarget = get(kpi, 'kpi_targets', []).filter(
      target => get(target, 'id', '') === get(resetValues, 'value')
    );
    const initialVals = {
      kpiTargets: resetValues,
      actualValue: kpiTarget[0]?.actual_value
    };
    setInitialValu(initialVals);
  };

  const ValidateTargets = value => {
    let error;
    if (!value) {
      error = 'Value required';
    }
    return error;
  };

  const ValidateActualValues = (value) => {
    let error;
  
    if (!value) {
      error = 'Value required';
    } else {
      const valueStr = value.toString();
      const numericValue = valueStr.replace(/,/g, '');
  
      if (isNaN(numericValue)) {
        error = 'Please enter digits only.';
      }
    }
    return error;
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalType(null);
  };

  const handleCommentIconClick = () => {
    setIsOptionsOpen(!isOptionsOpen);
  };

  const handleOptionSelect = (type: 'comment' | 'ada') => {
    setModalType(type);
    setEvidenceType(type); // Set evidence type using context
    setIsModalOpen(true);
    setIsOptionsOpen(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValu}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, values, validateForm }) => {
        const selectedKPI = targets.filter(
          kpiTarget => get(kpiTarget, 'id') === get(values, 'kpiTargets.value')
        );

        const isEitherCommentOrFileProvided = isCommentAccessed || hasAttachedFiles || adaEvidenceProvided;

        return (
          <Measures>
            <MeasureName>
              <IndicatorIcon color='#13C997' />
              <TitleText>{get(kpi, 'kpi_name')}</TitleText>
              <LabelText>{get(kpi, 'weighted_score', '-')}</LabelText>
              <PositiveChangeIcon y={0} />
            </MeasureName>
            <Field
              name='kpiTargets'
              component={DropDown}
              options={kpiTargets}
              width='90%'
              height='2.2em'
              backgroundColor='#F5F6FB'
              placeholder='Period'
              validate={ValidateTargets}
              onChange={values => handleReset(values)}
            />
            <Target>
              <TitleText>
                {numeral(get(selectedKPI, '[0]target', '-')).format('0,0')}
              </TitleText>
            </Target>
            <Actual>
              <TitleText>{get(kpi, 'kpi.score_type')}</TitleText>
              <Field
                name='actualValue'
                component={NumberInput}
                width='100%'
                validate={ValidateActualValues}
                backgroundColor='#fff'
                placeholder={selectedKPI[0]?.actual_value}
              />
            </Actual>
            <CommentIconContainer>
              <CommentIconButton onClick={handleCommentIconClick}>
                <CommentIcon />
              </CommentIconButton>
              {isOptionsOpen && (
                <OptionsDropdown ref={dropdownRef}>
                  <Option onClick={() => handleOptionSelect('comment')}>
                    <OptionIcon>💬</OptionIcon>
                    Add Comment
                  </Option>
                  <Option onClick={() => handleOptionSelect('ada')}>
                    <OptionIcon>📎</OptionIcon>
                    ADA Reference
                  </Option>
                </OptionsDropdown>
              )}
            </CommentIconContainer>
            <Button
              text='Submit'
              onClick={async () => {
                const errors = await validateForm();
                if (Object.keys(errors).length === 0 && isEitherCommentOrFileProvided) {
                  handleSubmit();
                } else {
                  console.log('Error:', errors);
                  toast.error('An error occurred submitting the form');
                }
              }}
              disabled={!isEitherCommentOrFileProvided}
            />
            {isModalOpen && modalType === 'comment' && (
              <Modal noHeader noFooter closeModal={closeModal} title='comment'>
                <CommentsModal
                  closeModal={closeModal}
                  url={`scorecardkpi/${get(kpi, 'id')}`}
                  scorecard_id={get(kpi, 'scorecard_id')}
                  scorecard_kpi_id={kpiId}
                  comment_type='measure_update'
                  onCommentAccessed={() => setIsCommentAccessed(true)}
                  onFileAttached={() => setHasAttachedFiles(true)}
                />
              </Modal>
            )}
            {isModalOpen && modalType === 'ada' && (
              <ADAReferenceModal
                  closeModal={closeModal}
                  onADAEvidenceProvided={(adaRef, folioNo) => {
                      setEvidenceData((prev) => ({
                          ...prev,
                          ada_ref: adaRef,
                          folio_no: folioNo,
                      }));
                      setAdaEvidenceProvided(true);
                  }}
              />
            )}
          </Measures>
        );
      }}
    </Formik>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -10px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
`;

const Measures = styled.div`
  display: grid;
  grid-template-columns: 0.3fr 0.16fr 0.18fr 0.21fr 0.02fr 0.135fr;
  grid-column-gap: 1em;
  align-items: center;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const MeasureName = styled.div`
  background-color: #f5f6fb;
  display: grid;
  grid-template-columns: 1fr 5fr 1fr 1fr;
  align-items: center;
  padding: 0.5em 0 0.5em 0.5em;
  border-radius: 5px;
  ${LabelText} {
    color: #13c997;
  }
`;

const Target = styled.div`
  background-color: #f5f6fb;
  padding: 0.5em 0 0.5em 0.5em;
`;

const Actual = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  align-items: center;
  padding-right: 2em;
`;

const CommentIconContainer = styled.span`
  position: relative;
  height: 1em;
  width: 1em;
  margin: 0.3em;
  display: flex;
  align-items: center;
  padding: 0.5em;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid #ccc;
  cursor: pointer;
`;

const CommentIconButton = styled.button`
  height: 2em;
  width: 2em;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid #ccc;
  background-color: white;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const OptionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 160px;
  max-width: 250px;
  padding: 8px 0;
  animation: ${slideDown} 0.3s ease-out;
  overflow: hidden;
  
  /* Responsive Adjustments */
  @media (max-width: 480px) {
    left: 0;
    transform: none;
    min-width: 100%;
  }
`;

const Option = styled.div`
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

const OptionIcon = styled.span`
  margin-right: 10px;
  font-size: 1.2em;
`;